export enum FontWeight {
  REGULAR = 400,
  MEDIUM = 500,
  SEMIBOLD = 600,
}

export const Colors = {
  // primary colors
  green: '#226752', // --color-green-100 (#226752)
  white: '#FFFFFF', // --color-neutral-0 (#FFFFFF)

  // secondary colors
  black: '#1d1d1d', // --color-neutral-800 (#252525)
  forest: '#374941', // --color-data-dark-green (#374941)
  stone: '#f2f2ef', // --color-neutral-50 (#F0EEEA)
  stone40: '#f2f2ef66', // --color-neutral-100 (#E5E4E0)
  stone40OnWhiteNoAlpha: '#fafaf9', // --color-neutral-25 (#F9F8F5)
  natural: '#fcf8f2', // --color-neutral-25 (#F9F8F5)

  // tertiary colors
  moss: '#cbd5ca', // --color-moss-50 (#C7D2C6)
  sunrise: '#fae3c3', // --color-orange-25 (#FDEAD7)
  blush: '#f0e7d5', // --color-yellow-25 (#FCF1CA)
  oak: '#656a47', // --color-data-olive (#656A47)
  greenBlue: '#8AD2BC', // --color-green-50 (#58AF8E)
  yellowOrange: '#F2D891', // --color-yellow-50 (#EFD167)

  // neutral colors
  charcoal: '#40403d', // --color-neutral-600 (#4F4F4F)
  darkGray: '#6F6F6B', // --color-neutral-500 (#6E6E6D)
  mediumGray: '#90908d', // --color-neutral-400 (#989796)
  gray: '#c9c9c5', // --color-neutral-200 (#D5D4D2)
  lightGray: '#e1e1dd', // --color-neutral-100 (#E5E4E0)

  // accent colors
  blue: '#4d6ca4', // --color-blue-100 (#327785)
  navy: '#5c5f9d', // --color-blue-300 (#1D3E44)
  purple: '#764c89', // --color-purple-100 (#733C7C)
  red: '#b95042', // --color-red-100 (#DF1C41)
  magenta: '#b54775', // --color-pink-100 (#993467)
  orange: '#ac590c', // --color-orange-100 (#E68A51)
  yellow: '#936d0d', // --color-yellow-100 (#F0B52F)
  teal: '#2f7d7d', // --color-blue-100 (#327785)

  // light accent colors
  lightBlue: '#f3f6ff', // --color-blue-0 (#E8F5F8)
  lightNavy: '#f0eeff', // --color-blue-0 (#E8F5F8)
  lightPurple: '#f8f2ff', // --color-purple-0 (#F9F0FA)
  lightRed: '#fff3f3', // --color-red-0 (#FFF0F3)
  lightMagenta: '#fff3f6', // --color-pink-0 (#FBEFF5)
  lightOrange: '#fbf3ea', // --color-orange-0 (#FEF6EE)
  lightYellow: '#fffaea', // --color-yellow-0 (#FEFAEC)
  lightGreen: '#f1fef5', // --color-green-0 (#F0F9F5)
  lightTeal: '#f0feff', // --color-blue-0 (#E8F5F8)

  // special colors
  champagne: '#faeecd', // --color-yellow-25 (#FCF1CA)
  accentGreen: '#457634', // --color-green-0 (#C6E7D7)
  accentRed: '#FFE0E0', // --color-red-0 (#FFF0F3)
  transparent: 'transparent',
}

export const Fonts = {
  display: {
    fontSize: 48,
    lineHeight: '120%',
    fontWeight: FontWeight.REGULAR,
    fontFamily: 'Werksatz!important',
    color: Colors.black,
  },
  display2: {
    fontSize: 32,
    lineHeight: '124%',
    fontWeight: FontWeight.MEDIUM,
    fontFamily: 'Werksatz!important',
    color: Colors.black,
  },
  display3: {
    fontSize: 20,
    lineHeight: '124%',
    fontWeight: FontWeight.MEDIUM,
    fontFamily: 'Werksatz!important',
    color: Colors.black,
  },
  h1: {
    fontSize: 26,
    lineHeight: '124%',
    fontWeight: FontWeight.MEDIUM,
    fontFamily: 'Werksatz!important',
    color: Colors.black,
  },
  h2: {
    fontSize: 20,
    lineHeight: '124%',
    fontWeight: FontWeight.MEDIUM,
    fontFamily: 'Werksatz!important',
    color: Colors.black,
  },
  h3: {
    fontSize: 16,
    lineHeight: '124%',
    fontWeight: FontWeight.MEDIUM,
    fontFamily: 'Werksatz!important',
    color: Colors.black,
  },
  // todo all of the body sizes are off of design but this will be a large lift to fix
  bodyLg: {
    // todo should be 16
    fontSize: 18,
    lineHeight: '155%',
    fontWeight: FontWeight.REGULAR,
    fontFamily: 'Inter!important',
  },
  bodyMd: {
    // todo should be 14
    fontSize: 16,
    lineHeight: '148%',
    fontWeight: FontWeight.REGULAR,
    fontFamily: 'Inter!important',
  },
  bodySm: {
    // todo should be 12
    fontSize: 14,
    lineHeight: '140%',
    fontWeight: FontWeight.REGULAR,
    fontFamily: 'Inter!important',
  },
  // todo this style does not exist in the design. Remove it once bodySm font is fixed
  bodyXs: {
    fontSize: 12,
    lineHeight: '140%',
    fontWeight: FontWeight.REGULAR,
    fontFamily: 'Inter!important',
  },
  buttonLg: {
    fontSize: '16px',
    lineHeight: '100%',
    fontWeight: FontWeight.MEDIUM,
    fontFamily: 'Inter!important',
    color: Colors.black,
  },
  buttonMd: {
    fontSize: '14px',
    lineHeight: '100%',
    fontWeight: FontWeight.MEDIUM,
    fontFamily: 'Inter!important',
    color: Colors.black,
  },
  buttonSm: {
    fontSize: '12px',
    lineHeight: '100%',
    fontWeight: FontWeight.MEDIUM,
    fontFamily: 'Inter!important',
    color: Colors.black,
  },
  eyebrow: {
    fontSize: 12,
    lineHeight: '140%',
    fontWeight: FontWeight.SEMIBOLD,
    textTransform: 'uppercase' as const,
    fontFamily: 'Werksatz!important',
    letterSpacing: '.03em',
    color: Colors.black,
  },
  link: {
    fontSize: '16px',
    lineHeight: '155%',
    fontWeight: FontWeight.MEDIUM,
    color: Colors.green,
    fontFamily: 'Inter!important',
  },
  secondaryLink: {
    fontSize: 16,
    lineHeight: '155%',
    fontWeight: FontWeight.REGULAR,
    textDecorationLine: 'underline',
    fontFamily: 'Inter!important',
    color: Colors.black,
  },
  actionLink: {
    fontSize: 16,
    lineHeight: '120%',
    fontWeight: FontWeight.MEDIUM,
    fontFamily: 'Inter!important',
    color: Colors.black,
  },
  actionLinkMd: {
    fontSize: 14,
    lineHeight: '120%',
    fontWeight: FontWeight.MEDIUM,
    fontFamily: 'Inter!important',
    color: Colors.black,
  },
}
